import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, BarChart, Menu, X, Home, ChevronDown, Brain, Monitor, Loader2, ClipboardCheck, Target, GraduationCap, Zap, Smartphone, Newspaper, CheckCircle } from 'lucide-react';
import { topicsApi, Theme } from '../../api/topics';

interface NavTheme extends Theme {
  icon: React.ComponentType<any>;
  path: string;
  status: 'available' | 'coming-soon';
}

export const Navbar: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const [isAIDropdownOpen, setIsAIDropdownOpen] = useState(false);
  const [isDigitalDropdownOpen, setIsDigitalDropdownOpen] = useState(false);
  const [isMediaDropdownOpen, setIsMediaDropdownOpen] = useState(false);
  const [themes, setThemes] = useState<NavTheme[]>([]);
  const [themesLoading, setThemesLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const aiDropdownRef = useRef<HTMLDivElement>(null);
  const digitalDropdownRef = useRef<HTMLDivElement>(null);
  const mediaDropdownRef = useRef<HTMLDivElement>(null);

  // Icon mapping based on theme slug
  const getIconForTheme = (slug: string): React.ComponentType<any> => {
    switch (slug) {
      case 'ai-basics':
      case 'ai-literacy':
        return Brain;
      case 'digital-literacy':
        return Monitor;
      default:
        return Brain;
    }
  };

  // Status mapping - for now, all themes from database are available
  const getStatusForTheme = (slug: string): 'available' | 'coming-soon' => {
    return 'available';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsThemeDropdownOpen(false);
      }
      if (aiDropdownRef.current && !aiDropdownRef.current.contains(event.target as Node)) {
        setIsAIDropdownOpen(false);
      }
      if (digitalDropdownRef.current && !digitalDropdownRef.current.contains(event.target as Node)) {
        setIsDigitalDropdownOpen(false);
      }
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target as Node)) {
        setIsMediaDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch themes from database
  useEffect(() => {
    const fetchThemes = async () => {
      try {
        setThemesLoading(true);
        const dbThemes = await topicsApi.getThemes();
        
        const transformedThemes: NavTheme[] = dbThemes.map(theme => ({
          ...theme,
          icon: getIconForTheme(theme.slug),
          status: getStatusForTheme(theme.slug),
          path: `/topics?theme=${theme.slug}`
        }));

        setThemes(transformedThemes);
      } catch (err) {
        console.error('Failed to fetch themes for navbar:', err);
        
        // Fallback to hardcoded themes
        setThemes([
          {
            id: 'fallback-ai',
            slug: 'ai-basics',
            title: 'AI Geletterdheid',
            description: 'Fundamentele concepten van kunstmatige intelligentie',
            icon: Brain,
            path: '/topics',
            status: 'available',
            created_at: new Date().toISOString()
          },
          {
            id: 'fallback-digital',
            slug: 'digitale-geletterdheid',
            title: 'Digitale Geletterdheid',
            description: 'Complete digitale geletterdheid training gebaseerd op Nederlandse standaarden en actuele bronnen.',
            icon: Monitor,
            path: '/topics?theme=digitale-geletterdheid',
            status: 'available',
            created_at: new Date().toISOString()
          }
        ]);
      } finally {
        setThemesLoading(false);
      }
    };

    fetchThemes();
  }, []);

  // AI Suite items for dropdown
  const aiSuiteItems = [
    { path: '/ai-literacy-scan', label: 'AI Geletterdheid Scan', icon: Target, description: 'Evalueer je AI-niveau' },
    { path: '/self-assessment', label: 'AI Kennistoets', icon: ClipboardCheck, description: 'Test je AI-kennis' },
    { path: '/topics', label: 'AI Training Modules', icon: GraduationCap, description: 'Ontwikkel je vaardigheden' },
  ];

  // Digital Suite items for dropdown
  const digitalSuiteItems = [
    { path: '/digital-literacy-scan', label: 'Digitale Geletterdheid Scan', icon: Target, description: 'Evalueer je digitale vaardigheden' },
    { path: '/topics?theme=digitale-kennistoets', label: 'Digitale Kennistoets', icon: ClipboardCheck, description: 'Test je digitale kennis met 20 vragen' },
    { path: '/topics?theme=digitale-geletterdheid', label: 'Digitale Training Modules', icon: GraduationCap, description: 'Ontwikkel je digitale vaardigheden' },
  ];

  // Media Suite items for dropdown
  const mediaSuiteItems = [
    { path: '/media-literacy-scan', label: 'Mediawijsheid Scan', icon: Target, description: 'Zelfbeoordeling van je mediawijsheid' },
    { path: '/media-wisdom-assessment', label: 'Mediawijsheid Kennistoets', icon: CheckCircle, description: 'Test je kennis met 27 vragen' },
    { path: '/topics?theme=mediawijsheid', label: 'Mediawijsheid Training', icon: GraduationCap, description: 'Ontwikkel je mediawijsheid vaardigheden' },
  ];

  const navItems = [
    { path: '/progress', label: 'Voortgang', icon: BarChart },
  ];

  return (
    <>
      <nav className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-gray-900">
                DigitalNext
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {/* Home Link */}
              <Link
                to="/"
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === '/'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Home className="w-4 h-4" />
                <span>Home</span>
              </Link>

              {/* AI Suite Dropdown */}
              <div className="relative" ref={aiDropdownRef}>
                <button
                  onClick={() => setIsAIDropdownOpen(!isAIDropdownOpen)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/ai-literacy-scan') || 
                    location.pathname.startsWith('/self-assessment') ||
                    (location.pathname.startsWith('/topics') && !location.pathname.includes('theme'))
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Zap className="w-4 h-4" />
                  <span>AI Suite</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                
                {isAIDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          AI Geletterdheid Suite
                        </h3>
                      </div>
                      {aiSuiteItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path || 
                          (item.path === '/topics' && location.pathname.startsWith('/topics'));
                        
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsAIDropdownOpen(false)}
                            className={`flex items-start space-x-3 px-4 py-3 text-sm transition-colors ${
                              isActive
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium">{item.label}</div>
                              <div className="text-xs text-gray-500">{item.description}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Digital Suite Dropdown */}
              <div className="relative" ref={digitalDropdownRef}>
                <button
                  onClick={() => setIsDigitalDropdownOpen(!isDigitalDropdownOpen)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/digital-literacy-scan') || 
                    location.pathname.startsWith('/digital-assessment') ||
                    (location.pathname.startsWith('/topics') && location.pathname.includes('digital-literacy'))
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  <span>Digitale Suite</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                
                {isDigitalDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Digitale Geletterdheid Suite
                        </h3>
                      </div>
                      {digitalSuiteItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path || 
                          (item.path === '/topics?theme=digital-literacy' && location.pathname.includes('digital-literacy'));
                        
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsDigitalDropdownOpen(false)}
                            className={`flex items-start space-x-3 px-4 py-3 text-sm transition-colors ${
                              isActive
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium">{item.label}</div>
                              <div className="text-xs text-gray-500">{item.description}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Media Suite Dropdown */}
              <div className="relative" ref={mediaDropdownRef}>
                <button
                  onClick={() => setIsMediaDropdownOpen(!isMediaDropdownOpen)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/media-literacy-scan')
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Newspaper className="w-4 h-4" />
                  <span>Media Suite</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                
                {isMediaDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Mediawijsheid Suite
                        </h3>
                      </div>
                      {mediaSuiteItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsMediaDropdownOpen(false)}
                            className={`flex items-start space-x-3 px-4 py-3 text-sm transition-colors ${
                              isActive
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium">{item.label}</div>
                              <div className="text-xs text-gray-500">{item.description}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Theme Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsThemeDropdownOpen(!isThemeDropdownOpen)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/topics') && location.pathname.includes('theme')
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Trainingsthema's</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                
                {isThemeDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      {themesLoading ? (
                        <div className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Thema's laden...</span>
                        </div>
                      ) : (
                        themes.map((theme) => {
                          const Icon = theme.icon;
                          const isAvailable = theme.status === 'available';
                          
                          return isAvailable ? (
                            <Link
                              key={theme.id}
                              to={theme.path}
                              onClick={() => setIsThemeDropdownOpen(false)}
                              className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                            >
                              <Icon className="w-4 h-4" />
                              <span>{theme.title}</span>
                            </Link>
                          ) : (
                            <div
                              key={theme.id}
                              className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-400 cursor-not-allowed"
                            >
                              <Icon className="w-4 h-4" />
                              <span>{theme.title}</span>
                              <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                                Binnenkort
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div 
            className="absolute inset-0 bg-black/20"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg">
            <div className="py-2">
              {/* Mobile Home Link */}
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center space-x-3 px-6 py-3 text-base font-medium transition-colors ${
                  location.pathname === '/'
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Home className="w-5 h-5" />
                <span>Home</span>
              </Link>

              <div className="border-t border-gray-200 mt-2 pt-2">
                {/* Mobile AI Suite Section */}
              <div className="px-6 py-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  AI Geletterdheid Suite
                </h3>
                {aiSuiteItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path || 
                    (item.path === '/topics' && location.pathname.startsWith('/topics'));
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-start space-x-3 px-3 py-3 text-base font-medium transition-colors rounded-lg ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <div>{item.label}</div>
                        <div className="text-xs text-gray-500">{item.description}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              </div>

              <div className="border-t border-gray-200 mt-2 pt-2">
                {/* Mobile Digital Suite Section */}
                <div className="px-6 py-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Digitale Geletterdheid Suite
                  </h3>
                  {digitalSuiteItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path || 
                      (item.path === '/topics?theme=digital-literacy' && location.pathname.includes('digital-literacy'));
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-start space-x-3 px-3 py-3 text-base font-medium transition-colors rounded-lg ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                          <div>{item.label}</div>
                          <div className="text-xs text-gray-500">{item.description}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              <div className="border-t border-gray-200 mt-2 pt-2">
                {/* Mobile Media Suite Section */}
                <div className="px-6 py-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Mediawijsheid Suite
                  </h3>
                  {mediaSuiteItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-start space-x-3 px-3 py-3 text-base font-medium transition-colors rounded-lg ${
                          isActive
                            ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                          <div>{item.label}</div>
                          <div className="text-xs text-gray-500">{item.description}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
              
              <div className="border-t border-gray-200 mt-2 pt-2">
                {/* Mobile Themes Section */}
                <div className="px-6 py-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Trainingsthema's
                  </h3>
                {themesLoading ? (
                  <div className="flex items-center space-x-3 px-3 py-2 text-base text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Thema's laden...</span>
                  </div>
                ) : (
                  themes.map((theme) => {
                    const Icon = theme.icon;
                    const isAvailable = theme.status === 'available';
                    
                    return isAvailable ? (
                      <Link
                        key={theme.id}
                        to={theme.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center space-x-3 px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon className="w-5 h-5" />
                        <span>{theme.title}</span>
                      </Link>
                    ) : (
                      <div
                        key={theme.id}
                        className="flex items-center space-x-3 px-3 py-2 text-base font-medium text-gray-400 cursor-not-allowed"
                      >
                        <Icon className="w-5 h-5" />
                        <span>{theme.title}</span>
                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                          Binnenkort
                        </span>
                      </div>
                    );
                  })
                )}
                </div>
              </div>
              
              <div className="border-t border-gray-200 mt-2 pt-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-6 py-3 text-base font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer for fixed navbar */}
      <div className="h-16" />
    </>
  );
};
